import _ from 'lodash';

import {addQueryToPath} from '../../../libs/shared/helpers/src/browser/urlHelpers';
import {
  TeammateAccountStatusesEnum,
  TeammateLicenseTypesEnum,
  TeammateModel,
} from '../../models/api/teammateModel';
import {TeammateOnlineStatusesEnum} from '../../store/data/teammates/onlineTeammatesSelectors';
import {buildTeammateDisplayName, doesTeammateMatchQuery} from '../modelHelpers';

/*
 * Search URL.
 */

export enum SearchTeammateSortsEnum {
  NAME = 'name',
  CREATED_AT = 'created_at',
  SCORING = 'scoring',
}
interface SearchTeammateOptions {
  /** Only list inboxes after the offset. */
  offset?: number;
  /** Limits the number of inboxes returned in the response. */
  limit?: number;
  /** Team ids to include. */
  teamIds?: ReadonlyArray<number>;
  /** Account statuses to include. */
  accountStatuses?: ReadonlyArray<TeammateAccountStatusesEnum>;
  /** License types to include. */
  licenseTypes?: ReadonlyArray<TeammateLicenseTypesEnum>;
  /** Teammate ids to exclude. */
  excludedTeammateIds?: ReadonlyArray<number>;
  /** Sort by a given field. */
  sort?: SearchTeammateSortsEnum;
}

export function buildTeammateSearchUrl(companyUrl: string, query: string, options: SearchTeammateOptions) {
  return addQueryToPath(
    `${companyUrl}/search_teammate/${encodeURIComponent(query.toLowerCase())}`,
    serializeSearchTeammateOptions(options),
  );
}

function serializeSearchTeammateOptions(options: SearchTeammateOptions) {
  return {
    offset: options.offset || 0,
    limit: options.limit || 50,
    team_ids: options.teamIds,
    account_statuses: options.accountStatuses,
    license_types: options.licenseTypes,
    excluded_teammate_ids: options.excludedTeammateIds,
    sort: options.sort,
  };
}

/*
 * Online status.
 */

export function getOnlineStatusForTeammate(
  teammate: TeammateModel | undefined,
  onlineTeammateIds: ReadonlySet<number> | undefined,
): TeammateOnlineStatusesEnum | undefined {
  if (!teammate) {
    return undefined;
  }

  if (!onlineTeammateIds) {
    return TeammateOnlineStatusesEnum.UNKNOWN;
  }

  if (onlineTeammateIds.has(teammate.id)) {
    return TeammateOnlineStatusesEnum.ONLINE;
  }

  return TeammateOnlineStatusesEnum.OFFLINE;
}

export function filterMentionableTeammates(
  teammates: ReadonlyArray<TeammateModel>,
  query: string,
  limit: number,
) {
  const lowerCaseQuery = query.toLowerCase();
  return _(teammates)
    .filter((t) => doesTeammateMatchQuery(t, query))
    .sortBy((t) => {
      // We need to sort by the same logic as we filtered.
      const displayName = buildTeammateDisplayName(t).toLowerCase();
      const alias = t.alias.toLowerCase();
      return !alias.startsWith(lowerCaseQuery) && !displayName.startsWith(lowerCaseQuery);
    })
    .take(limit)
    .value();
}

export function didTeammateSignupWithOauth(teammate: TeammateModel) {
  return teammate.isOffice365Signup || teammate.isGoogleSignup;
}
